import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Megaphone } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Announcements = () => {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        await axios.post(`${API}/seed-announcements`).catch(() => {});
        const res = await axios.get(`${API}/announcements`);
        setSlides(res.data);
      } catch (err) {
        console.error("Failed to fetch announcements:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);

  const goTo = useCallback((idx, dir) => {
    setDirection(dir);
    setCurrent(idx);
  }, []);

  // Auto-advance
  useEffect(() => {
    if (slides.length <= 1) return;
    timerRef.current = setInterval(() => {
      goTo((prev) => prev, 1);
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [slides.length, goTo]);

  const prev = () => {
    clearInterval(timerRef.current);
    const idx = (current - 1 + slides.length) % slides.length;
    goTo(idx, -1);
  };

  const next = () => {
    clearInterval(timerRef.current);
    const idx = (current + 1) % slides.length;
    goTo(idx, 1);
  };

  if (loading || slides.length === 0) return null;

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 600 : -600, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -600 : 600, opacity: 0 }),
  };

  return (
    <section
      id="announcements"
      data-testid="announcements-section"
      className="py-16 md:py-24 bg-gradient-to-br from-red-50/50 via-white to-amber-50/30 relative overflow-hidden"
    >
      <div className="bubble-decoration bg-red-400 w-48 h-48 -top-24 right-10 animate-pulse-soft" />

      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Megaphone size={20} className="text-red-500" />
            <span className="font-accent text-2xl text-amber-600">Stay Informed</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-stone-900">
            Announcements
          </h2>
        </div>

        {/* Carousel */}
        <div className="relative" data-testid="announcements-carousel">
          <div className="overflow-hidden rounded-[2rem] shadow-[0_20px_60px_rgb(0,0,0,0.08)] bg-white">
            <div className="relative aspect-[16/9]">
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.img
                  key={current}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ type: "tween", duration: 0.4, ease: "easeInOut" }}
                  src={slides[current]?.image_url}
                  alt={slides[current]?.title || "Announcement"}
                  className="absolute inset-0 w-full h-full object-contain bg-white"
                  data-testid={`announcement-slide-${current}`}
                  draggable={false}
                />
              </AnimatePresence>
            </div>
          </div>

          {/* Nav arrows */}
          {slides.length > 1 && (
            <>
              <button
                onClick={prev}
                data-testid="carousel-prev-btn"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 shadow-lg flex items-center justify-center text-stone-700 hover:bg-white hover:text-red-600 transition-all z-10"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={next}
                data-testid="carousel-next-btn"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 shadow-lg flex items-center justify-center text-stone-700 hover:bg-white hover:text-red-600 transition-all z-10"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          {/* Dots */}
          {slides.length > 1 && (
            <div className="flex justify-center gap-2 mt-6" data-testid="carousel-dots">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    clearInterval(timerRef.current);
                    goTo(i, i > current ? 1 : -1);
                  }}
                  data-testid={`carousel-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    i === current ? "w-8 bg-red-600" : "w-2.5 bg-stone-300 hover:bg-stone-400"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
